import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
    amountInWords,
    formatDate,
    formatFloat,
} from '../../../../../../helpers';
import {
    OrderQuotationsPageQuery,
    PrintableQuotationQuery,
    usePrintableQuotationLazyQuery,
} from '../../../../../../services';
import inopackLogo from '../../../../../app/inopack-app-bar/logo.png';

// The trigger: a row selected from the list. Only its id is used — the printable
// runs its own dedicated PrintableQuotation query (not the list query, not the
// dialog query) so the printout carries exactly the fields it needs regardless of
// what the list happens to select.
type TSelectedQuotation =
    | OrderQuotationsPageQuery['paginatedOrderQuotations']['docs'][number]
    | null;

type PrintableQuotationDoc = NonNullable<
    PrintableQuotationQuery['getOrderQuotation']
>;

type PrintableQuotationProductDoc =
    PrintableQuotationDoc['order_quotation_products'][number];

// Brand palette lifted from the reference cotización: the green frame/rule and a
// muted section-rule green. Recreated in CSS rather than by cropping the PDF's
// frame image (deliberate — see the feature plan, Phase 5).
const BRAND_GREEN = '#6DAE3F';
const RULE_GREEN = '#B7C7B5';
const INK = '#333';
const MUTED = '#666';
// A soft, thin neutral rule for the line-item table only. Deliberately grey (not
// the green RULE_GREEN) and lighter so the table's own gridlines don't read as
// section rules the way the green underline did.
const TABLE_LINE = '#E4E4E4';

// The per-line importe. There is no server-side `total` ResolveField on
// OrderQuotationProduct, so it is computed with the exact formula the backend uses
// for the quotation subtotal (kilo_price·kilos + group_price·groups). A line
// carries one price or the other.
function lineTotal(orp: PrintableQuotationProductDoc): number {
    return orp.kilo_price * orp.kilos + orp.group_price * orp.groups;
}

// The per-line unit price, ALWAYS expressed per kilo so the column is
// unambiguous. A kilo-priced line uses kilo_price directly; a group-priced line
// is converted to its per-kg equivalent (importe / kilos, which equals
// group_price / group_weight). This keeps a single "Precio por kg" column even
// when lines mix kilo and bulto pricing, instead of showing a $/bulto figure
// under the same header. See the feature plan / quotation pricing note.
function unitPricePerKilo(orp: PrintableQuotationProductDoc): number {
    if (orp.kilo_price) {
        return orp.kilo_price;
    }
    // Group-priced: divide the line importe by its kilos. Guard the degenerate
    // zero-kilos case (nothing to divide by) by falling back to the raw price.
    return orp.kilos > 0 ? lineTotal(orp) / orp.kilos : orp.group_price;
}

// Bultos / Kilos print as whole numbers in the reference (8, 3,600) — show no
// decimals when the quantity is integral, two when it is not, so a fractional
// weight is never silently dropped.
function formatQty(x: number): string {
    return Number.isInteger(x) ? formatFloat(x, 0) : formatFloat(x, 2);
}

// A bold label / plain value row, the shape used by both DATOS DEL CLIENTE and
// CONDICIONES COMERCIALES in the reference.
function InfoRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <Box sx={{ display: 'flex', py: 0.35 }}>
            <Box sx={{ width: '150px', fontWeight: 'bold', flexShrink: 0 }}>
                {label}
            </Box>
            <Box sx={{ flexGrow: 1 }}>{value}</Box>
        </Box>
    );
}

// A section heading with the muted-green underline rule from the reference.
function SectionHeading({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                fontWeight: 'bold',
                fontSize: '12px',
                letterSpacing: '0.5px',
                color: INK,
                borderBottom: `1.5px solid ${RULE_GREEN}`,
                pb: 0.3,
                mt: 6,
                mb: 1.2,
            }}
        >
            {children}
        </Box>
    );
}

/**
 * A printable cotización that mirrors the reference PDF
 * (Cotizacion_Empaques_Murguia_0001): a green branded frame with the inopack
 * logo, COTIZACIÓN title, folio/fecha, DATOS DEL CLIENTE, the line table with
 * bold product names, importe con letra, and CONDICIONES COMERCIALES. It renders
 * inside `.printable-block` and prints via `window.print()` the moment its data
 * lands — the same plumbing as PrintableReceipt.
 *
 * Deliberately NOT a copy of the remisión receipt: one copy (not two stacked
 * copies split by a dashed line), no F/N tax marker, and no signature lines. The
 * IVA breakdown shows only when the quotation is marked IVA-visible; moneda is
 * fixed to MXN (no FX infrastructure) but printed. The frame is CSS, and only the
 * inopack logo is used — no cropped PDF images.
 */
export function PrintableQuotation({
    orderQuotation,
    printRequestId,
}: {
    orderQuotation: TSelectedQuotation;
    printRequestId: number;
}) {
    // Every click carries a monotonically increasing request id. Apollo may not
    // invoke onCompleted again when a repeated network response is identical, so
    // the print lifecycle is driven by our request instead of that callback.
    const [readyPrintRequestId, setReadyPrintRequestId] = useState(0);
    // This lazy query remains mounted and can refetch after any sales mutation.
    // Remember which request already printed so a changed Apollo `data` object
    // cannot reopen the browser dialog without a new export click.
    const consumedPrintRequestId = useRef(0);
    const [loadQuotation, { data }] = usePrintableQuotationLazyQuery({
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (!orderQuotation || printRequestId === 0) return;

        let active = true;
        void loadQuotation({
            variables: { OrderQuotationId: orderQuotation.id },
        }).then((result) => {
            if (active && result.data?.getOrderQuotation) {
                setReadyPrintRequestId(printRequestId);
            }
        });

        return () => {
            active = false;
        };
    }, [orderQuotation, printRequestId, loadQuotation]);

    useEffect(() => {
        if (
            readyPrintRequestId !== printRequestId ||
            consumedPrintRequestId.current === printRequestId ||
            !data?.getOrderQuotation
        ) {
            return;
        }

        // Wait one paint after Apollo commits the fetched document. The request
        // id changes on every click, including repeated clicks on the same row.
        const animationFrame = window.requestAnimationFrame(() => {
            // Consume before opening print. Once the browser dialog closes,
            // cache-driven refetches see this request as already handled.
            consumedPrintRequestId.current = printRequestId;
            window.print();
        });
        return () => window.cancelAnimationFrame(animationFrame);
    }, [readyPrintRequestId, printRequestId, data]);

    const quotation = data?.getOrderQuotation;
    if (!orderQuotation || !quotation) {
        return null;
    }

    const queriedProducts = quotation.order_quotation_products ?? [];
    // The selected list row already carries the same line fields. Retain it as a
    // final row-level fallback so a partial dedicated-query response cannot turn
    // a quotation with products into an empty table.
    const products = queriedProducts.length
        ? queriedProducts
        : orderQuotation.order_quotation_products ?? [];
    const productDescriptions = new Map(
        (data?.getProducts ?? []).map((product) => [
            product.id,
            product.external_description,
        ]),
    );
    (orderQuotation.order_quotation_products ?? []).forEach((line) => {
        if (
            line.product_id != null &&
            line.product?.external_description &&
            !productDescriptions.has(line.product_id)
        ) {
            productDescriptions.set(
                line.product_id,
                line.product.external_description,
            );
        }
    });
    // The unit-price column is ALWAYS per kilo (group-priced lines are converted,
    // see unitPricePerKilo), so the header is a fixed "Precio por kg" — it never
    // has to switch to an ambiguous "Precio unitario" that mixes $/kg and $/bulto.
    const unitPriceHeader = 'Precio por kg';

    return (
        <Box
            className={'printable-block'}
            sx={{
                lineHeight: 1.3,
                fontFamily: `'Open Sans', sans-serif`,
                fontSize: '12px',
                color: INK,
            }}
        >
            <section slot="pdf-content">
                {/* A fixed print-only frame is anchored to the page rather than
                    participating in document layout. Unlike a 100vh minHeight,
                    it cannot add a few pixels to the content height and create a
                    blank second page. */}
                <Box
                    aria-hidden
                    sx={{
                        display: 'none',
                        '@media print': {
                            display: 'block',
                            position: 'fixed',
                            inset: '8px',
                            border: `3px solid ${BRAND_GREEN}`,
                            borderRadius: '4px',
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                        },
                    }}
                />
                {/* The green branded frame, recreated in CSS. printColorAdjust
                    keeps the green when the browser prints (borders print by
                    default, but this guards themes that strip color). */}
                <Box
                    sx={{
                        border: `3px solid ${BRAND_GREEN}`,
                        borderRadius: '4px',
                        p: 3,
                        m: 1,
                        boxSizing: 'border-box',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact',
                        '@media print': {
                            border: 'none',
                            // Reserve room for the bottom-pinned contact footer so
                            // the last section can't print underneath it.
                            pb: 6,
                        },
                    }}
                >
                    {/* Header: logo + title on the left, folio / fecha on the
                        right */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box>
                            <Box
                                component={'img'}
                                src={inopackLogo}
                                alt={'Inopack'}
                                sx={{ height: '46px', mb: 3 }}
                            />
                            <Box
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    letterSpacing: '1px',
                                    color: INK,
                                }}
                            >
                                COTIZACIÓN
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Box>
                                <Box
                                    component={'span'}
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Folio:{' '}
                                </Box>
                                {quotation.order_code}
                            </Box>
                            <Box>
                                <Box
                                    component={'span'}
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Fecha:{' '}
                                </Box>
                                {formatDate(quotation.date)}
                            </Box>
                        </Box>
                    </Box>

                    {/* DATOS DEL CLIENTE */}
                    <SectionHeading>DATOS DEL CLIENTE</SectionHeading>
                    <InfoRow
                        label={'Cliente'}
                        value={quotation.account?.name ?? 'No proporcionado'}
                    />
                    <InfoRow
                        label={'RFC'}
                        value={quotation.account?.rfc || 'No proporcionado'}
                    />
                    <InfoRow
                        label={'Domicilio'}
                        value={quotation.account?.address || 'No proporcionado'}
                    />

                    {/* DETALLE DE LA COTIZACIÓN */}
                    <SectionHeading>DETALLE DE LA COTIZACIÓN</SectionHeading>
                    <Table
                        size={'small'}
                        padding={'none'}
                        sx={{
                            // MUI TableCell owns its color, so the outer Box's
                            // dark ink does not override a dark theme. Without
                            // this, rows print as light text on white paper.
                            // The soft, hairline row rule is set here (over MUI's
                            // default divider) so every line-item line is thin and
                            // grey — distinct from the green section rules.
                            '& .MuiTableCell-root': {
                                color: INK,
                                borderBottom: `0.5px solid ${TABLE_LINE}`,
                            },
                        }}
                    >
                        <TableHead sx={{ display: 'table-row-group' }}>
                            <TableRow sx={{ pageBreakInside: 'avoid' }}>
                                {[
                                    {
                                        label: 'Producto',
                                        align: 'left' as const,
                                    },
                                    {
                                        label: 'Bultos',
                                        align: 'right' as const,
                                    },
                                    { label: 'Kilos', align: 'right' as const },
                                    {
                                        label: unitPriceHeader,
                                        align: 'right' as const,
                                    },
                                    {
                                        label: 'Importe',
                                        align: 'right' as const,
                                    },
                                ].map((col) => (
                                    <TableCell
                                        key={col.label}
                                        style={{
                                            textAlign: col.align,
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            color: INK,
                                            borderBottom: `0.5px solid ${TABLE_LINE}`,
                                            padding: '4px 8px',
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {products.map((orp) => {
                                // product_id is the persisted, authoritative
                                // distinction. For a linked line, prefer its
                                // nested product and fall back to the direct
                                // catalog query so a resolver miss cannot leave
                                // the exported description blank.
                                const isFreeLine = orp.product_id == null;
                                const productDescription = isFreeLine
                                    ? orp.proposed_description
                                    : orp.product?.external_description ||
                                      productDescriptions.get(
                                          orp.product_id!,
                                      ) ||
                                      `Producto #${orp.product_id}`;
                                return (
                                    <TableRow key={orp.id}>
                                        <TableCell
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                padding: '4px 8px',
                                            }}
                                        >
                                            {productDescription}
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                textAlign: 'right',
                                                fontSize: '12px',
                                                padding: '4px 8px',
                                            }}
                                        >
                                            {formatQty(orp.groups)}
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                textAlign: 'right',
                                                fontSize: '12px',
                                                padding: '4px 8px',
                                            }}
                                        >
                                            {formatQty(orp.kilos)}
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                textAlign: 'right',
                                                fontSize: '12px',
                                                padding: '4px 8px',
                                            }}
                                        >
                                            $
                                            {formatFloat(unitPricePerKilo(orp))}
                                        </TableCell>
                                        <TableCell
                                            style={{
                                                textAlign: 'right',
                                                fontSize: '12px',
                                                padding: '4px 8px',
                                            }}
                                        >
                                            ${formatFloat(lineTotal(orp))}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    {/* Totals — Subtotal / IVA rows only when IVA is visible;
                        otherwise a single bold TOTAL as in the reference */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 1,
                        }}
                    >
                        <Box sx={{ minWidth: '260px' }}>
                            {quotation.require_tax ? (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            px: 1,
                                            py: 0.25,
                                        }}
                                    >
                                        <Box>Subtotal:</Box>
                                        <Box>
                                            ${formatFloat(quotation.subtotal)}
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            px: 1,
                                            py: 0.25,
                                        }}
                                    >
                                        <Box>IVA (16%):</Box>
                                        <Box>${formatFloat(quotation.tax)}</Box>
                                    </Box>
                                </>
                            ) : null}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontWeight: 'bold',
                                    // Match the line-item table's own rule — a
                                    // thin, soft grey hairline — rather than the
                                    // heavier green section rule, so the total
                                    // reads as the table's footer, not a new
                                    // section divider.
                                    borderTop: `0.5px solid ${TABLE_LINE}`,
                                    mt: 0.5,
                                    px: 1,
                                    py: 0.5,
                                }}
                            >
                                <Box>TOTAL:</Box>
                                <Box>${formatFloat(quotation.total)}</Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Importe con letra */}
                    <Box sx={{ mt: 1 }}>
                        <Box component={'span'} sx={{ fontWeight: 'bold' }}>
                            Importe con letra:{' '}
                        </Box>
                        <Box component={'span'} sx={{ fontStyle: 'italic' }}>
                            {amountInWords(quotation.total)}
                        </Box>
                    </Box>

                    {/* CONDICIONES COMERCIALES */}
                    <SectionHeading>CONDICIONES COMERCIALES</SectionHeading>
                    <InfoRow label={'Moneda'} value={'Pesos mexicanos (MXN)'} />
                    <InfoRow
                        label={'Condiciones de pago'}
                        value={quotation.payment_terms || 'No especificado'}
                    />
                    <InfoRow
                        label={'Tiempo de entrega'}
                        value={
                            quotation.estimated_delivery_date
                                ? formatDate(quotation.estimated_delivery_date)
                                : 'No especificado'
                        }
                    />
                    <InfoRow
                        label={'Vigencia de la cotización'}
                        value={
                            quotation.expiration_date
                                ? formatDate(quotation.expiration_date)
                                : 'Sin vencimiento'
                        }
                    />

                    {/* Notes (optional) */}
                    {quotation.notes ? (
                        <Box sx={{ mt: 1.5 }}>
                            <Box sx={{ fontWeight: 'bold' }}>Observaciones</Box>
                            <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                {quotation.notes}
                            </Box>
                        </Box>
                    ) : null}

                    {/* Fiscal disclaimer */}
                    <Box
                        sx={{
                            mt: 2,
                            fontSize: '10px',
                            fontStyle: 'italic',
                            color: MUTED,
                        }}
                    >
                        Esta cotización no constituye un comprobante fiscal.
                    </Box>

                    {/* Contact footer, recreated in CSS from the reference frame.
                        Pinned to the bottom of the page when printing — the green
                        rule rides with it — so the contact line and its rule sit at
                        the foot of the sheet regardless of how short the content is.
                        On screen it stays in normal flow at the end of the block. */}
                    <Box
                        sx={{
                            mt: 4,
                            pt: 1,
                            borderTop: `1.5px solid ${BRAND_GREEN}`,
                            textAlign: 'center',
                            fontSize: '10px',
                            color: MUTED,
                            WebkitPrintColorAdjust: 'exact',
                            printColorAdjust: 'exact',
                            '@media print': {
                                position: 'fixed',
                                left: '24px',
                                right: '24px',
                                bottom: '20px',
                                mt: 0,
                            },
                        }}
                    >
                        Yucatán, México · administracion@grupoinopack.com ·
                        www.grupoinopack.com
                    </Box>
                </Box>
            </section>
        </Box>
    );
}
